import React from 'react';

const Privacy = () => {
  return (
    <div className="flex justify-center items-center min-h-screen p-4">
      <div className="shadow-md rounded-2xl p-8 max-w-4xl bg-[#112D4E]">
        <h1 className="text-3xl font-bold mb-6 text-center">
          Privacy <span className="text-[#19BFB7]">Policy</span>
        </h1>
        <p className="text-white text-center mb-4">Effective Date: 2025</p>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">1. Introduction</h2>
          <p className="text-white">
            AIAURA ("Company," "we," "our," or "us") is committed to protecting your privacy in compliance with the UK General Data Protection Regulation (UK GDPR), the EU General Data Protection Regulation (EU GDPR), and the Data Protection Act 2018. This Privacy Policy explains how we collect, use, store, and protect your personal data when you use our website and services. By accessing or using AIAURA, you confirm that you have read, understood, and agreed to this Privacy Policy. If you do not agree, please refrain from using our services.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">2. Data Controller</h2>
          <p className="text-white">
            AIAURA acts as the data controller for personal data collected through our website and services, as defined under the UK GDPR and EU GDPR. If you have any questions regarding this policy or your data, you may contact our Data Protection Officer (DPO) at <a href="mailto:Admin@aiaura.co.uk" className="text-[#19BFB7] hover:underline">Admin@aiaura.co.uk</a>.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">3. Data We Collect</h2>
          <ul className="list-disc list-inside text-white ml-4">
            <li>Account Information: Full name, email address, phone number, and login details.</li>
            <li>Profile Information: Resume, work experience, skills, and education details, as provided by users.</li>
            <li>Employer & Recruiter Data: Company name, business details, and job listings submitted through the platform.</li>
            <li>Communication Data: Messages, emails, and interactions with other users or AIAURA support.</li>
            <li>Technical Data: IP address, browser type, operating system, and other tracking data via cookies and analytics tools.</li>
            <li>Payment Data: Billing details for paid services, processed via third-party PCI-DSS-compliant payment providers.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">4. Lawful Basis for Processing</h2>
          <ul className="list-disc list-inside text-white ml-4">
            <li>Contractual Necessity: Processing data to provide recruitment platform services.</li>
            <li>Legitimate Interests: Enhancing security, improving user experience, and fraud prevention.</li>
            <li>Legal Obligation: Compliance with employment, anti-fraud, and data protection regulations.</li>
            <li>Consent: Where required, such as for direct marketing communications.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">5. How We Use Your Data</h2>
          <ul className="list-disc list-inside text-white ml-4">
            <li>To provide, manage, and improve our recruitment platform.</li>
            <li>To verify accounts and prevent fraudulent activities.</li>
            <li>To facilitate job applications and candidate-employer interactions.</li>
            <li>To send necessary service-related communications and support responses.</li>
            <li>To comply with legal, tax, and regulatory obligations.</li>
            <li>To analyze and enhance our AI-driven recommendations while mitigating bias.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">6. Data Sharing and Third-Party Services</h2>
          <p>We do not sell or rent personal data. However, we may share data with:</p>
          <ul className="list-disc list-inside text-white ml-4">
            <li>Employers and Recruiters: Limited profile data for job applications.</li>
            <li>Service Providers: Hosting, security, analytics, and payment processing partners.</li>
            <li>Legal Authorities: When required by law, regulation, or lawful request.</li>
            <li>Business Transfers: In case of a merger, acquisition, or asset sale.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">7. International Data Transfers</h2>
          <p className="text-white">
            Where data is transferred outside the UK or EU, we ensure compliance via UK/EU-approved Standard Contractual Clauses (SCCs), adequacy decisions by the UK/EU Commission, and legally approved safeguards under applicable laws.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">8. Data Retention</h2>
          <p className="text-white">
            We retain personal data only as long as necessary for lawful purposes, considering regulatory, legal, and contractual obligations. Users may request data deletion as outlined in Section 9.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">9. User Rights Under UK & EU GDPR</h2>
          <ul className="list-disc list-inside text-white ml-4">
            <li>Right to Access: Obtain confirmation of whether we process your data.</li>
            <li>Right to Rectification: Request corrections to inaccurate data.</li>
            <li>Right to Erasure (Right to be Forgotten): Request data deletion where legally permitted.</li>
            <li>Right to Restriction of Processing: Request limitations on data processing in certain situations.</li>
            <li>Right to Object: Object to data processing where our legal basis is legitimate interest.</li>
            <li>Right to Data Portability: Receive a copy of your data in a structured, machine-readable format.</li>
            <li>Right to Withdraw Consent: Withdraw consent for specific data uses (e.g., marketing).</li>
            <li>Right to Lodge a Complaint: File a complaint with the UK Information Commissioner's Office (ICO) or relevant EU supervisory authority if you believe your rights have been violated.</li>
          </ul>
          <p>To exercise your rights, contact us at <a href="mailto:admin@aiaura.co.uk" className="text-[#19BFB7] hover:underline">admin@aiaura.co.uk</a>.</p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">10. Data Security</h2>
          <p className="text-white">
            We implement industry-standard security measures, including encryption, access controls, secure hosting, and regular audits, to protect your data. Despite these safeguards, no system is completely secure, and users should take precautions when sharing personal information online.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">11. Cookies and Tracking Technologies</h2>
          <p className="text-white">
            AIAURA uses cookies and similar technologies for website functionality and analytics. Users can manage cookie preferences via their browser settings or opt out of non-essential cookies through our cookie management tool.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">12. Changes to This Privacy Policy</h2>
          <p className="text-white">
            We reserve the right to update this Privacy Policy at any time in response to legal, regulatory, or operational changes. Significant updates will be communicated via email or website notifications. Continued use of AIAURA services after modifications constitutes acceptance of the updated policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-2xl font-semibold mb-2 text-[#19BFB7]">13. Contact Information</h2>
          <p className="text-white">
            For privacy-related inquiries, data access requests, or concerns, please contact <a href="mailto:admin@aiaura.co.uk" className="text-[#19BFB7] hover:underline">admin@aiaura.co.uk</a>.
          </p>
        </section>
      </div>
    </div>
  );
};

export default Privacy;
